/**
 * The module registry: one entry per full-page panel.
 *
 * The nav (both arrangements), the badge counts, the invite routing and the panel guides all key
 * off this one table, exactly as they did in the Angular client. Two things from that version are
 * gone on purpose:
 *
 *   `tone`  — every panel used to paint itself a different pastel. Hunsy now runs the fleet
 *             `default` theme, whose whole claim is that one palette carries the app; ten local
 *             accents is a second design system wearing one name.
 *   the SVG icon set — React Native has no inline SVG without react-native-svg, and a glyph is
 *             what the fleet's own nav components already use.
 */
export type ScreenId =
  | 'home'
  | 'missions'
  | 'signals'
  | 'list'
  | 'gratitude'
  | 'sync'
  | 'dates'
  | 'wrapped'
  | 'score'
  | 'spark'
  | 'pulse';

/** The panel's full-screen explainer, opened only by the ⓘ in its header. */
export interface Guide {
  /** One sentence: what this panel is for. */
  objective: string;
  /** Two to four short steps. Imperative, no fluff. */
  how: string[];
  /** The finding that justifies the panel existing at all. */
  claim: string;
  source: string;
}

export interface ModuleDef {
  id: ScreenId;
  /** Short label for the tab. */
  title: string;
  /** The name the panel shows in its own header, which the guide repeats. */
  heading: string;
  /** One line under the title, in the module's own voice. */
  sub: string;
  glyph: string;
  /** Panels the partner can invite the other into (Sync sessions, co-op, Spark decks). */
  invitable: boolean;
  guide: Guide;
}

/**
 * Spark ships dark.
 *
 * Not deleted: the screen, the API routes and the `ScreenId` member all still work, and turning it
 * back on is putting this entry back in the array below. It is out because a first install should
 * not open on a tab of intimacy questions — the panel needs the trust the rest of the app earns
 * first — and because a tab nobody opens is a tab that quietly rots.
 */
export const SPARK_MODULE: ModuleDef =
  {
    id: 'spark',
    title: 'Spark',
    heading: 'Spark',
    sub: 'Only ever what you both want',
    glyph: '✦',
    invitable: true,
    guide: {
      objective:
        'Private yes-or-no answers about intimacy. Only overlap is ever revealed: if you say yes and they say no, nobody ever learns you said yes.',
      how: [
        'Answer "into it" or "pass" privately. A pass is invisible forever.',
        'Matches appear only where you both said yes.',
        'Warmer tiers unlock only if you both quietly opt in — a one-sided opt-in is never disclosed.',
      ],
      claim:
        'Sexual communication is strongly associated with relationship and sexual satisfaction; the barrier is almost always the risk of asking first. Removing that risk is the entire mechanism here.',
      source: 'Mallory et al. 2019 meta-analysis',
    },
  };

export const MODULES: ModuleDef[] = [
  {
    id: 'home',
    title: 'Home',
    heading: 'Home',
    sub: 'Your pairing, your pings, your rules',
    glyph: '⌂',
    invitable: false,
    guide: {
      objective:
        'Your base: who you are playing as, and the only controls over how the game is allowed to interrupt you.',
      how: [
        'Turn on notifications once, per device — they carry a module name and a count, never content.',
        'Set quiet hours so nothing pings overnight.',
        'Mute nudges any time; invites and reveals still come through.',
      ],
      claim:
        'A shared game only survives if it never becomes a nag. Everything the app sends is capped, quiet-hours aware, and phrased as coming from the game — never as your partner waiting on you.',
      source: 'Hunsy design rule',
    },
  },
  {
    id: 'missions',
    title: 'Missions',
    heading: 'Missions',
    sub: 'Small moves that count',
    glyph: '◎',
    invitable: false,
    guide: {
      objective:
        'Small, doable prompts to turn toward your partner today. Some come from the game; some were quietly planted by something they told the app. You will never be able to tell which — that is deliberate.',
      how: [
        'Do it, then tap "Did it" to bank the points.',
        'Tap "Not for me" to skip — skipping is silent and reported to nobody.',
        'If you are behind on the week, your missions pay double until you catch up.',
      ],
      claim:
        'Turning toward small bids for connection is the strongest observed predictor of couples staying together.',
      source: 'Gottman, on turning toward bids',
    },
  },
  {
    id: 'signals',
    title: 'Signals',
    heading: 'Signals',
    sub: 'Say it here — it is never quoted',
    /* ▣ and ⇄ rather than ⌾ and ◍: at 17px in a dock, four of the ten glyphs were a circle with
       something small inside it, and a badge count sitting over the corner made them one shape.
       A filled square and a two-way arrow are the two of that four that had a plainer shape
       available — Sync's is the one its own COMPARE badge already uses. */
    glyph: '▣',
    invitable: false,
    guide: {
      objective:
        'The one place to say the honest thing — a request, a desire, a frustration. Your words are never shown to your partner. Not paraphrased, not softened: never shown.',
      how: [
        'Write it plainly. Nobody is reading it but the AI drafting a plan.',
        'Review the plan in one pass — edit any wording, strike any step, then approve.',
        'That approval is the only one. The app picks the moments after that, and even you will not know when.',
      ],
      claim:
        'Conversations that open with criticism predict relationship failure. Saying it here converts a harsh start-up into anonymous missions, planted ideas and quiet questions instead.',
      source: 'Gottman, “harsh startup”',
    },
  },
  {
    id: 'list',
    title: 'List',
    heading: 'Our List',
    sub: 'The shared load, fairly split',
    glyph: '✓',
    invitable: false,
    guide: {
      objective:
        'A plain shared to-do list. Deliberately boring — it is the thing that makes you both open the app on an ordinary Tuesday.',
      how: [
        'Add anything either of you needs done.',
        'Tick it when it is done; the list records who did it.',
        'Clearing someone else’s item is worth points, up to a weekly cap.',
      ],
      claim:
        'Perceived fairness in the division of household labour predicts relationship satisfaction — visibility of who does what is most of the battle.',
      source: 'Household-labour research',
    },
  },
  {
    id: 'gratitude',
    title: 'Thanks',
    heading: 'Gratitude',
    sub: 'Notice it out loud',
    glyph: '♥',
    invitable: false,
    guide: {
      objective:
        'One appreciation a day, said out loud to your partner. The only panel where what you write is shown to them exactly as you wrote it — because that is the point.',
      how: [
        'Name one specific thing they did. Small counts; specific beats generous.',
        'Drop it once a day.',
        'Keep a run going — five days in a row pays a bonus.',
      ],
      claim:
        'Expressed gratitude functions like a booster shot for relationships, predicting connection and stability weeks later — among the largest effects in the literature.',
      source: 'Algoe, Gable & Maisel 2010',
    },
  },
  {
    id: 'sync',
    title: 'Sync',
    heading: 'Sync',
    sub: 'How well do you know each other?',
    glyph: '⇄',
    invitable: true,
    guide: {
      objective:
        'A short round of questions you both answer privately, plus a guess at what the other said. Nothing is visible until you have both locked in.',
      how: [
        'Start a round — it invites your partner automatically.',
        'Answer honestly, then guess their answer.',
        'Lock in. When you both have, everything reveals at once and correct guesses score.',
      ],
      claim:
        'Knowing your partner’s inner world — their "Love Map" — predicts relationship success, and structured question protocols reliably increase closeness between two people.',
      source: 'Gottman on love maps; Aron’s 36-questions protocol',
    },
  },
  {
    id: 'dates',
    title: 'Dates',
    heading: 'Date Night',
    sub: 'Sealed cards. Scratch to commit.',
    glyph: '◈',
    invitable: false,
    guide: {
      objective:
        'A wall of sealed date cards. You can see how long it takes, what it costs and how much prep it needs — never what it is, until you commit.',
      how: [
        'Read the three icons: time, budget, prep.',
        'Scratch one only if you mean it — whatever is underneath, you are both doing it.',
        'Afterwards, log a line and a photo into the scrapbook.',
      ],
      claim:
        'Novel, engaging shared activities measurably raise relationship quality — more than merely pleasant familiar ones. Committing before you know what it is removes the negotiation that kills most date nights.',
      source: 'Aron et al. 2000',
    },
  },
  {
    id: 'wrapped',
    title: 'Wrapped',
    heading: 'Wrapped',
    sub: 'A gift, peeled a tile at a time',
    glyph: '▨',
    invitable: false,
    guide: {
      objective:
        'Wrap a photo for your partner. They see a blurred teaser and peel it a tile at a time by doing things the game allows — never anything vulnerable.',
      how: [
        'Choose a photo, a tile count and a price from the menu.',
        'Withdraw it any time before the last tile; you get a ping first and can hold it a day.',
        'Choose vanish-after-viewing or keep in the scrapbook. Unpeeled wraps expire.',
      ],
      claim:
        'Looking forward to something raises the enjoyment of it; committing a small act to unlock it is the same device that makes Date Night work.',
      source: 'Anticipation research; scratch-to-commit',
    },
  },
  {
    id: 'score',
    title: 'Score',
    heading: 'Scoreboard',
    sub: 'This week’s friendly season',
    glyph: '★',
    invitable: true,
    guide: {
      objective:
        'The week as a game. Points buy influence — a pick, a veto, bragging rights — and never outcomes. Nobody can spend points on your behaviour.',
      how: [
        'Pick a secret agenda; matching your partner’s pays you both a jackpot.',
        'Stake a personal goal on Monday or Tuesday: double or nothing.',
        'Run the reveal on Sunday. Win by a little and you take the pick — win by twenty and nobody does.',
      ],
      claim:
        'Play and shared humour track couple satisfaction. The scoring bands are lifted from Fox in the Forest: overwhelming victory is punished, so running up the score is never the winning move.',
      source: 'Play research; Fox in the Forest scoring bands',
    },
  },
  {
    id: 'pulse',
    title: 'Pulse',
    heading: 'Pulse',
    sub: 'Rate the week, privately',
    glyph: '◔',
    invitable: false,
    guide: {
      objective:
        'A one-number check-in on the week. Your rating and your note stay yours; the two of you only ever see which direction the week moved.',
      how: [
        'Rate the week 1 to 5.',
        'Add a private note to future-you if you want one.',
        'When you have both checked in, the shared view shows a ring for which way the week moved — never the numbers.',
      ],
      claim:
        'A regular structured check-in surfaces drift early, while it is still a conversation rather than a crisis.',
      source: 'Modelled on Gottman’s State of the Union meeting',
    },
  },
];

/**
 * Panel metadata by id.
 *
 * Falls through to SPARK_MODULE so the dark screen still has a heading and a guide if anything
 * routes to it — a `Panel` that cannot find its own module throws, and "unreachable" is a claim
 * about today's nav rather than about the code.
 */
export const moduleById = (id: string): ModuleDef | undefined =>
  MODULES.find((m) => m.id === id) ?? (id === 'spark' ? SPARK_MODULE : undefined);

/** Whether a module has a tab. `openPanel` uses this — a push must never open a dark panel. */
export const isNavigable = (id: string): boolean => MODULES.some((m) => m.id === id);
